import React, { useState, useEffect } from 'react';
import { customersAPI, salesAPI } from '../services/api';
import CustomerForm from '../components/CustomerForm';
import ActionModal from '../components/ActionModal';
import CobranzaModal from '../components/CobranzaModal';
import Toast from '../components/Toast';
import '../components/CustomerForm.css';
import './OrdersManagement.css';

const CustomersPage = () => {
    // ==================== ESTADOS ====================
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState(null);

    // Modal Crear/Editar
    const [showFormModal, setShowFormModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // ActionModal (menú contextual kebab)
    const [actionCustomer, setActionCustomer] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);

    // Modal de confirmación de eliminación
    const [customerToDelete, setCustomerToDelete] = useState(null);

    // ━━━ Modal de Cobros ━━━
    const [cobranzaCustomer, setCobranzaCustomer] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // ==================== EFECTOS ====================
    useEffect(() => { loadCustomers(); }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const showToast = (message, type = 'success') => setToast({ message, type });

    // ==================== CARGAR CLIENTES ====================
    const loadCustomers = async () => {
        setIsLoading(true);
        try {
            const response = await customersAPI.getAll();
            const data = response.data.data?.customers || response.data.customers || [];
            setCustomers(data);
        } catch (error) {
            console.error(error);
            showToast('Error al cargar clientes', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // ==================== CRUD CLIENTES ====================
    const handleSaveCustomer = async (formData) => {
        try {
            if (selectedCustomer) {
                await customersAPI.update(selectedCustomer.id, formData);
                showToast('Cliente actualizado', 'success');
            } else {
                await customersAPI.create(formData);
                showToast('Cliente creado', 'success');
            }
            setShowFormModal(false);
            loadCustomers();
        } catch (error) {
            showToast(error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    const handleDelete = (customer) => {
        // Cierra el ActionModal y abre el modal de confirmación
        setShowActionModal(false);
        setCustomerToDelete(customer);
    };

    const confirmDelete = async () => {
        if (!customerToDelete) return;
        try {
            await customersAPI.delete(customerToDelete.id);
            showToast('Cliente eliminado', 'success');
            setCustomerToDelete(null);
            loadCustomers();
        } catch (error) {
            showToast('Error al eliminar', 'error');
            setCustomerToDelete(null);
        }
    };

    const openFormModal = (customer = null) => {
        setSelectedCustomer(customer);
        setShowFormModal(true);
    };

    // ==================== ACTION MODAL ====================
    const getCustomerActions = () => {
        const current = actionCustomer;
        if (!current) return [];
        return [
            {
                icon: '✏️', title: 'Editar Cliente', subtitle: 'Modificar datos personales',
                variant: 'edit',
                onClick: () => setTimeout(() => openFormModal(current), 350)
            },
            {
                icon: '🗑️', title: 'Eliminar', subtitle: 'Borrar permanentemente',
                variant: 'delete',
                onClick: () => handleDelete(current)
            }
        ];
    };

    const handleOpenActionModal = (e, customer) => {
        e.stopPropagation();
        setActionCustomer(customer);
        setShowActionModal(true);
    };

    const handleCloseActionModal = () => {
        setShowActionModal(false);
        setTimeout(() => setActionCustomer(null), 300);
    };

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // FLUJO DE COBROS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    const openCobranzaModal = (customer) => setCobranzaCustomer(customer);
    const closeCobranzaModal = () => setCobranzaCustomer(null);
    const handlePaidCallback = () => loadCustomers(); // Refrescar tabla tras cobro

    // ==================== FILTRO BÚSQUEDA ====================
    const filteredCustomers = customers.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.document_number?.includes(searchTerm)
    );

    // ── Tarjeta móvil ──
    const MobileCustomerCard = ({ c }) => {
        const debt = parseFloat(c.total_debt || 0);
        return (
            <div className="order-card-mobile">
                <div className="order-card-top">
                    <span className="order-id" style={{ color: '#3b82f6', flex: 1 }}>{c.name}</span>
                    {debt > 0 && (
                        <span className="status-badge" style={{ background: '#fef2f2', color: '#dc2626', fontWeight: 700 }}>
                            S/. {debt.toFixed(2)}
                        </span>
                    )}
                    <button className="kebab-btn" onClick={(e) => handleOpenActionModal(e, c)}>⋮</button>
                </div>
                <div className="order-card-bottom">
                    {c.document_number && <span>📄 {c.document_number}</span>}
                    {c.phone && <span>📞 {c.phone}</span>}
                    {c.email && <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{c.email}</span>}
                    {debt > 0 && (
                        <button
                            onClick={() => openCobranzaModal(c)}
                            style={{
                                marginTop: 4, background: '#16a34a', color: '#fff',
                                border: 'none', borderRadius: 6, padding: '4px 12px',
                                cursor: 'pointer', fontWeight: 600
                            }}
                        >
                            💰 Cobrar
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // ==================== RENDER ====================
    return (
        <div className="customers-page" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="page-header">
                <h2>👥 Gestión de Clientes</h2>
                <p className="orders-header-subtitle">Administra tu cartera de clientes</p>
            </div>

            {/* Barra de Acciones */}
            <div className="filters-container" style={{ justifyContent: 'space-between' }}>
                <div className="search-container" style={{ margin: 0, width: '60%' }}>
                    <input
                        type="text"
                        placeholder="🔍 Buscar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <button onClick={() => openFormModal(null)} className="create-order-btn">➕ Nuevo Cliente</button>
            </div>

            {/* Lista / Tabla */}
            {isLoading
                ? <div className="loading-container"><p>⏳ Cargando...</p></div>
                : filteredCustomers.length === 0
                    ? <div className="empty-container"><p>No hay clientes</p></div>
                    : isMobile
                        ? (
                            <div className="orders-cards-container">
                                {filteredCustomers.map(c => <MobileCustomerCard key={c.id} c={c} />)}
                            </div>
                        ) : (
                            <div className="table-container">
                                <table className="orders-table">
                                    <thead>
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Documento</th>
                                            <th>Teléfono</th>
                                            <th>Ubicación</th>
                                            <th>Tipo</th>
                                            <th style={{ color: '#dc2626' }}>💳 Deuda</th>
                                            <th className="center">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCustomers.map(c => {
                                            const debt = parseFloat(c.total_debt || 0);
                                            return (
                                                <tr key={c.id}>
                                                    <td><strong>{c.name}</strong><br /><small>{c.email}</small></td>
                                                    <td>{c.document_number || '-'}</td>
                                                    <td>{c.phone || '-'}</td>
                                                    <td>{c.departamento || '-'}</td>
                                                    <td>
                                                        <span className="status-badge" style={{ background: '#e3f2fd', color: '#0d47a1' }}>
                                                            {c.tipo_cliente || 'General'}
                                                        </span>
                                                    </td>
                                                    {/* ━━━ COLUMNA DEUDA ━━━ */}
                                                    <td>
                                                        {debt > 0
                                                            ? <span style={{ color: '#dc2626', fontWeight: 700 }}>S/. {debt.toFixed(2)}</span>
                                                            : <span style={{ color: '#16a34a' }}>✓ Al día</span>
                                                        }
                                                    </td>
                                                    <td className="center" style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                                                        {/* ━━━ BOTÓN COBRAR ━━━ */}
                                                        {debt > 0 && (
                                                            <button
                                                                onClick={() => openCobranzaModal(c)}
                                                                style={{
                                                                    background: '#16a34a', color: '#fff', border: 'none',
                                                                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                                                                    fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                💰 Cobrar
                                                            </button>
                                                        )}
                                                        <button className="kebab-btn" onClick={(e) => handleOpenActionModal(e, c)}>⋮</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
            }

            {/* Modal de Cobros — componente reutilizable */}
            <CobranzaModal
                customer={cobranzaCustomer}
                onClose={closeCobranzaModal}
                onPaid={handlePaidCallback}
            />

            {/* Modal Crear/Editar Cliente */}
            {showFormModal && (
                <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
                    <div className="modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h2 style={{ margin: 0 }}>{selectedCustomer ? '✏️ Editar Cliente' : '👤 Nuevo Cliente'}</h2>
                            <button onClick={() => setShowFormModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
                        </div>
                        <CustomerForm initialData={selectedCustomer} onSubmit={handleSaveCustomer} />
                    </div>
                </div>
            )}

            <ActionModal
                isOpen={showActionModal}
                onClose={handleCloseActionModal}
                title="Acciones de Cliente"
                itemName={actionCustomer?.name}
                itemSubtitle={actionCustomer ? `Doc: ${actionCustomer.document_number || 'N/A'}` : ''}
                actions={getCustomerActions()}
            />

            {/* Modal de confirmación de eliminación */}
            {customerToDelete && (
                <div className="modal-overlay" style={{ zIndex: 999999 }} onClick={() => setCustomerToDelete(null)}>
                    <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{ borderBottom: '3px solid #ef4444' }}>
                            <h2 style={{ color: '#dc2626', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ⚠️ Eliminar Cliente
                            </h2>
                            <button className="close-button" onClick={() => setCustomerToDelete(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ margin: '0 0 12px 0', color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>
                                ¿Eliminar a{' '}
                                <span style={{ color: '#dc2626' }}>{customerToDelete.name}</span>?
                            </p>
                            <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                <strong style={{ color: '#dc2626' }}>Esta acción no se puede deshacer.</strong>
                            </p>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => setCustomerToDelete(null)}
                                    style={{
                                        flex: 1, padding: '11px', border: '1px solid #cbd5e1',
                                        background: 'white', color: '#475569', fontWeight: '600',
                                        borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    style={{
                                        flex: 1, padding: '11px', border: 'none',
                                        background: '#dc2626', color: 'white', fontWeight: '700',
                                        borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem',
                                        transition: 'background 0.2s'
                                    }}
                                >
                                    Sí, eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default CustomersPage;